import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, InputNumber, Radio, Space } from 'antd';
import _ from 'lodash';

import { updateFilterItemInList } from '../../../../../../../helpers/utils/reports';
import { useActions } from '../../../../../hooks/useActions';
import { useTypedSelector } from '../../../../../hooks/useTypedSelector';

/** Маппинг ID -> единицы измерения */
const UNIT_NAME_MAP: Record<string, string> = {
    count_orders: 'заказов',
    count_payments: 'платежей',
    uwe_duration_the_webinar: 'Минут',
};

/** ID фильтров, для которых единица измерения — валюта аккаунта */
const CURRENCY_UNIT_IDS = new Set([
    'payment_data',
    'sum_order',
    'uoe_sum_order',
    'first_order_sum',
    'upe_sum',
    'order_payment_sum',
    'sum',
]);

/** ID фильтров, допускающих отрицательные значения */
const IDS_WITH_NEGATIVE = ['number_days_access'];

/** Интерфейс данных фильтра */
interface FilterData {
    start: number | null;
    end: number | null;
    [key: string]: unknown;
}

/** Props для NumberRangePicker */
interface NumberRangePickerProps {
    isModal?: boolean;
    isLocalMode?: boolean;
    localFilters?: any[];
    setLocalFilters?: (filters: any[]) => void;
    disabled?: boolean;
    id: string;
    index?: number;
    modalVisibleHandler: (visible: boolean) => void;
    itemData?: FilterData;
    list?: boolean;
    name?: string;
    [key: string]: unknown;
}

const NumberRangePicker: React.FC<NumberRangePickerProps> = ({
    isModal = false,
    isLocalMode = false,
    localFilters,
    setLocalFilters,
    disabled = false,
    id,
    index,
    modalVisibleHandler,
    itemData,
    list,
    name,
    ...props
}) => {
    const { filters: globalFilters } = useTypedSelector((state) =>
        isModal ? state.filter.dashboardModal : state.filter
    );
    const currency = useTypedSelector((state) => state.meta.currency);
    const filters = isLocalMode ? localFilters || [] : globalFilters;
    const { setFilters, setFilter, setMetaFilterUpdate } = useActions();

    const [filterMode, setFilterMode] = useState(1);
    const isPeriodFilter = name?.includes('Период от');

    // Приводим id к строке для безопасной работы с методами строк
    const safeId = String(id ?? '');

    // Вычисляем ID с учётом режима include/exclude
    const filterId = useMemo(() => {
        if (filterMode === 2) {
            return safeId.includes('not_') ? safeId : `not_${safeId}`;
        }
        return safeId.includes('not_') ? safeId.split('not_')[1] : safeId;
    }, [safeId, filterMode]);

    // Инициализация режима из id
    useEffect(() => {
        setFilterMode(safeId.includes('not_') ? 2 : 1);
    }, [safeId]);

    // Определяем режим редактирования
    const isEditing = useMemo(() => {
        return Boolean(list && itemData);
    }, [list, itemData]);

    // Мемоизированные начальные данные фильтра
    const initialFilter = useMemo((): FilterData => {
        if (itemData && list) {
            return itemData;
        }
        return { start: null, end: null, name, ...props };
    }, [itemData, list, props, name]);

    const [currentData, setCurrentData] = useState<FilterData>(initialFilter);

    // Синхронизация currentData при изменении itemData
    useEffect(() => {
        if (itemData && list) {
            setCurrentData(itemData);
        }
    }, [itemData, list]);

    const onApply = useCallback(() => {
        const _currentData = {
            ...currentData,
            id: filterId
        };

        if (isLocalMode && setLocalFilters) {
            // Локальный режим
            if (isEditing && index !== undefined) {
                let _filters = _.cloneDeep(filters);
                _filters = updateFilterItemInList(_filters, index, _currentData);
                setLocalFilters(_filters);
            } else {
                setLocalFilters([...filters, _currentData]);
            }
        } else {
            // Глобальный режим
            if (isEditing && index !== undefined) {
                let _filters = _.cloneDeep(filters);
                _filters = updateFilterItemInList(_filters, index, _currentData);
                setFilters(isModal ? { isModal, data: _filters } : _filters);
            } else {
                setFilter({ ..._currentData, isModal });
            }
            setMetaFilterUpdate({ value: true });
        }

        modalVisibleHandler(false);
    }, [currentData, filterId, isEditing, index, filters, isLocalMode, setLocalFilters, isModal, setFilters, setFilter, setMetaFilterUpdate, modalVisibleHandler]);

    const onChange = useCallback((type: 'start' | 'end', val: number | null) => {
        setCurrentData((prevState) => ({
            ...prevState,
            [type]: val
        }));
    }, []);

    // Мемоизированные минимальные значения
    const minStart = useMemo(() => {
        if (isPeriodFilter) return 1;
        if (IDS_WITH_NEGATIVE.includes(id)) return -Infinity;
        return 0;
    }, [isPeriodFilter, id]);

    const minEnd = useMemo(() => {
        if (IDS_WITH_NEGATIVE.includes(id)) return -Infinity;
        return 0;
    }, [id]);

    const unitName = CURRENCY_UNIT_IDS.has(id) ? (currency || '₽') : (UNIT_NAME_MAP[id] || 'дней');

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' && !disabled) {
            e.preventDefault();
            onApply();
        }
    }, [disabled, onApply]);

    return (
        <div onKeyDown={handleKeyDown}>
            <div className="picker-radio-group">
                <Radio.Group disabled={disabled} onChange={(e) => setFilterMode(e.target.value)} value={filterMode}>
                    <Radio value={1}>Включить</Radio>
                    <Radio value={2}>Исключить</Radio>
                </Radio.Group>
            </div>

            <Space>
                <span>от</span>
                <InputNumber 
                    disabled={disabled} 
                    value={currentData.start} 
                    onChange={(val) => onChange('start', val)} 
                    min={minStart} 
                    style={{ width: 64 }} 
                />
                <span>до</span>
                <InputNumber 
                    disabled={disabled} 
                    value={currentData.end} 
                    onChange={(val) => onChange('end', val)} 
                    min={minEnd} 
                    style={{ width: 64 }} 
                />
                <span>{unitName}</span>
            </Space>
            <br />
            <Button disabled={disabled} type="primary" className="picker-apply-btn" onClick={onApply}>
                Применить
            </Button>
        </div>
    );
};

export default NumberRangePicker;
